import React, { useState, useEffect } from 'react';
import { 
  ArrowUpIcon, 
  ArrowDownIcon, 
  BoltIcon, 
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import SystemOverviewPanel from '../components/SupervisoryDashboard/SystemOverviewPanel';
import { EnvironmentalImpactCard } from '../components/DailyReports/EnvironmentalImpactCard';
import AssetConnectivityMap from '../components/SupervisoryDashboard/AssetConnectivityMap';
import PerformanceTable from '../components/SupervisoryDashboard/PerformanceTable';
import { 
  systemOverviewService, 
  SystemOverviewData, 
  EnvironmentalImpactData, 
  AssetStatusData, 
  PerformanceMetrics,
  DailyPerformanceData 
} from '../services/systemOverviewService';

const SupervisoryDashboard: React.FC = () => {
  const [systemData, setSystemData] = useState<SystemOverviewData | null>(null);
  const [environmentalData, setEnvironmentalData] = useState<EnvironmentalImpactData | null>(null);
  const [assetData, setAssetData] = useState<AssetStatusData | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceMetrics | null>(null);
  const [tableData, setTableData] = useState<DailyPerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState('plant-001');

  useEffect(() => {
    loadDashboardData();
  }, [selectedPlant]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [system, environmental, assets, performance, table] = await Promise.all([
        systemOverviewService.getSystemOverview(selectedPlant),
        systemOverviewService.getEnvironmentalImpact(selectedPlant),
        systemOverviewService.getAssetStatus(selectedPlant),
        systemOverviewService.getPerformanceMetrics(selectedPlant),
        systemOverviewService.getPerformanceTable(selectedPlant)
      ]);

      setSystemData(system);
      setEnvironmentalData(environmental);
      setAssetData(assets);
      setPerformanceData(performance);
      setTableData(table);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const performanceMetrics = [
    {
      label: 'Energy Export/Import',
      value: `${performanceData?.energy_export_import || 0} kWh`,
      icon: ArrowUpIcon,
      color: 'text-blue-600'
    },
    {
      label: 'Specific Yield',
      value: performanceData?.specific_yield.toFixed(2) || '0.00',
      icon: ChartBarIcon,
      color: 'text-green-600'
    },
    {
      label: 'CUF',
      value: `${performanceData?.cuf || 0}%`,
      icon: BoltIcon,
      color: 'text-yellow-600'
    },
    {
      label: 'PR',
      value: `${performanceData?.pr || 0}%`,
      icon: CheckCircleIcon,
      color: 'text-purple-600'
    },
    {
      label: 'Energy Loss',
      value: `${performanceData?.energy_loss || 0} kWh`,
      icon: ArrowDownIcon,
      color: 'text-red-600'
    },
    {
      label: 'Plant Availability',
      value: `${performanceData?.plant_availability || 0}%`,
      icon: CheckCircleIcon,
      color: 'text-green-600'
    },
    {
      label: 'Grid Availability',
      value: `${performanceData?.grid_availability || 0}%`,
      icon: ExclamationTriangleIcon,
      color: 'text-orange-600'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold gradient-neural">Ground-Mounted System Overview</h1>
          <p className="text-slate-400 mt-2">Supervisory Dashboard & Environmental Intelligence</p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={selectedPlant}
            onChange={(e) => setSelectedPlant(e.target.value)}
            className="ai-input text-sm py-2 px-3"
          >
            <option value="plant-001">Solar Plant 001</option>
            <option value="plant-002">Solar Plant 002</option>
            <option value="plant-003">Solar Plant 003</option>
          </select>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel: System Overview */}
        <div className="space-y-6">
          {systemData && (
            <SystemOverviewPanel data={systemData} loading={loading} />
          )}
          
          {environmentalData && (
            <EnvironmentalImpactCard impact={{
              co2_saved_tons: environmentalData.co2_saved_tons,
              trees_equivalent: environmentalData.trees_saved_k * 1000,
              cars_off_road_days: environmentalData.trees_saved_k * 2000
            }} />
          )}
        </div>

        {/* Center Panel: Performance Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Performance & Environmental Metrics
          </h3>
          <div className="space-y-3">
            {performanceMetrics.map((metric, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <metric.icon className={`w-4 h-4 ${metric.color}`} />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {metric.label}
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {metric.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel: Asset Connectivity */}
        <div>
          {assetData && (
            <AssetConnectivityMap data={assetData} loading={loading} />
          )}
        </div>
      </div>

      {/* Bottom Panel: Performance Table */}
      <div>
        <PerformanceTable data={tableData} loading={loading} />
      </div>
    </div>
  );
};

export default SupervisoryDashboard;